import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

const TRANSLATION_BASE_URL = import.meta.env.VITE_TRANSLATION_BASE_URL;

// Track initialization to prevent duplicate loading
let isInitialized = false;

// Cache for loaded translations to avoid redundant fetches
const translationCache: Record<string, any> = {};

// Define a function to load translations with error handling
const loadTranslations = async (lng: string) => {
  // Return cached translations if available
  if (translationCache[lng]) {
    console.log(`Using cached translations for ${lng}`);
    return translationCache[lng];
  }

  const path1 = `${TRANSLATION_BASE_URL}labels/locale-${lng}.json`;
  const path2 = `${TRANSLATION_BASE_URL}tags/locale-lt-${lng}.json`;

  try {
    // Fetch both JSON files
    const [data1, data2] = await Promise.all([
      fetch(path1, { cache: "force-cache" }).then((res) => {
        if (!res.ok)
          throw new Error(`Failed to load translations from ${path1}`);
        return res.json();
      }),
      fetch(path2, { cache: "force-cache" }).then((res) => {
        if (!res.ok)
          throw new Error(`Failed to load translations from ${path2}`);
        return res.json();
      }),
    ]);

    // Merge both translation objects
    const mergedTranslations = { ...data1, ...data2 };

    // Cache the translations
    translationCache[lng] = mergedTranslations;

    return mergedTranslations;
  } catch (error) {
    console.error(`Error loading translations for ${lng}:`, error);
    return translationCache[lng] || {};
  }
};

// Function to check if a translation file exists
// This is used to determine if we should load a specific language
// and to fall back to a default language if the requested one is not available
// This function uses a HEAD request to check for the existence of the translation file
// It returns true if the file exists, false otherwise
const translationFileExists = async (lng: string): Promise<boolean> => {
  const labelUrl = `${TRANSLATION_BASE_URL}labels/locale-${lng}.json`;
  const tagUrl = `${TRANSLATION_BASE_URL}tags/locale-lt-${lng}.json`;

//  console.log(`Checking translation file for ${lng}: ${url}`); 
 
  try {
    const [labelRes, tagRes] = await Promise.all([
      fetch(labelUrl, { method: "HEAD" }),
      fetch(tagUrl, { method: "HEAD" }),
    ]);
    // console.log(`Translation file for ${lng} exists:`, res.ok);
    
    return labelRes.ok && tagRes.ok;
  } catch (error) {
    console.error(`Error checking translation file for ${lng}:`, error);
    return false;
  }
};
 

// Initialize i18next
i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    fallbackLng: "en-US",
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    // Disable built-in backend to prevent automatic loading of translation.json
    // We'll handle loading translations manually
    resources: {}, // Start with empty resources
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lng",
      lookupLocalStorage: "lng",
      caches: ["localStorage"],
    },
    debug: false, // Set to true only for debugging
  });

// Define a function to change the language
export const changeLanguage = async (lng: string): Promise<void> => {
  try {
    // Only load translations if they're not already loaded
    if (!i18n.hasResourceBundle(lng, "translation")) {
      const translations = await loadTranslations(lng);
      i18n.addResourceBundle(lng, "translation", translations, true, true);
    }

    // Set the new language
    await i18n.changeLanguage(lng);

    // Update localStorage
    localStorage.setItem("i18nextLng", lng);
  } catch (error) {
    console.error(`Failed to change language to ${lng}:`, error);
    if (lng !== "en-US" && !i18n.hasResourceBundle("en-US", "translation")) {
      console.warn(`Falling back to default language (en-US)`);
      await changeLanguage("en-US");
    }
  }
};

// Initialize with the default language
// export const initI18n = async (): Promise<void> => {
//   // Prevent multiple initializations
//   if (isInitialized) return;
//   const language =localStorage.getItem("i18nextLng") || navigator.language || "en-US";   
//   await changeLanguage(language);
//   isInitialized = true;
// };

// Initialize i18n with the default language. This function checks for the stored language in localStorage or uses the browser's default language
export const initI18n = async (): Promise<void> => {
  if (isInitialized) return;
  // Check for stored language in localStorage or use browser's default language
  const storedLang = localStorage.getItem("i18nextLng");
  const browserLang = navigator.language || "en-US";
  let languageToUse = storedLang || browserLang;
 // Normalize the language code to a standard format (e.g., en-US)
  const isSupported = await translationFileExists(languageToUse);
  // console.log(`Using language: ${languageToUse}, Supported: ${isSupported}`);
  // If the translation file does not exist for the selected language, fall back to en-US
  // This ensures that we always have a valid language to use
  if (!isSupported) {
    console.warn(`Translation file not found for ${languageToUse}, falling back to en-US`);
    languageToUse = "en-US";
  }
  // Change the language using the changeLanguage function
  await changeLanguage(languageToUse);
  isInitialized = true;
};

// Only auto-initialize in browser environment to avoid SSR issues
if (typeof window !== "undefined") {
  // Use a flag to track if initialization has started
  let initStarted = false;

  if (!initStarted) {
    initStarted = true;
    initI18n().catch((error) => {
      console.error("Failed to initialize i18n:", error);
    });
  }
}

export default i18n;
